import { useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import {
	APP_INITIATED,
	CURRENT_ACTIVE_BATCH,
	FIREBASE_ID,
	KEYCLOAK_ID_TOKEN,
	LOGGED_IN_USER_ROLES,
	ME_PROFILE,
	SELECTED_ROLE,
	ROLES_WITH_MODULES,
	LOGGED_IN_USER_PRIVILEGES,
} from '../shared/Constants';
import { MeProfile } from '../shared/MySrcm';
import { Batch, UserRole } from '../shared/VolunteerApplicationServiceTypes';
import {
	RoleModulesMapping,
	RoleDetails,
	RolePrivilege,
} from '../shared/RoleBasedTypes';

export default function useUserSession() {
	const {
		storedValue: keycloakToken,
		setValue: setKeycloakToken,
		deleteValue: deleteKeycloakToken,
	} = useLocalStorage<string | null>(KEYCLOAK_ID_TOKEN, null);
	const {
		storedValue: meProfile,
		setValue: setMeProfile,
		deleteValue: deleteMeProfile,
	} = useLocalStorage<MeProfile | null>(ME_PROFILE, null);
	const {
		storedValue: srcmFirebaseId,
		setValue: setSrcmFirebaseId,
		deleteValue: deleteSrcmFirebaseId,
	} = useLocalStorage<string | null>(FIREBASE_ID, null);
	const {
		storedValue: loggedInUserRoles,
		setValue: setLoggedInUserRoles,
		deleteValue: deleteLoggedInUserRoles,
	} = useLocalStorage<RoleDetails[] | null>(LOGGED_IN_USER_ROLES, null);
	const {
		storedValue: selectedRole,
		setValue: setSelectedRole,
		deleteValue: deleteSelectedRole,
	} = useLocalStorage<UserRole | null>(SELECTED_ROLE, null);
	const {
		storedValue: allRolesWithModules,
		setValue: setAllRolesWithModules,
		deleteValue: deleteAllRolesWithModules,
	} = useLocalStorage<RoleModulesMapping[] | null>(ROLES_WITH_MODULES, null);
	const {
		storedValue: loggedInUserPrivileges,
		setValue: setLoggedInUserPrivileges,
		deleteValue: deleteLoggedInUserPrivileges,
	} = useLocalStorage<RolePrivilege[] | null>(LOGGED_IN_USER_PRIVILEGES, null);
	const {
		storedValue: appInitiated,
		setValue: setAppInitiated,
		deleteValue: deleteAppInitiated,
	} = useLocalStorage<boolean | null>(APP_INITIATED, null);
	const {
		storedValue: currentActiveBatch,
		setValue: setCurrentActiveBatch,
		deleteValue: deleteCurrentActiveBatch,
	} = useLocalStorage<Batch | null>(CURRENT_ACTIVE_BATCH, null);

	const clearKeycloakToken = useCallback(() => {
		setKeycloakToken(null);
		deleteKeycloakToken();
	}, [setKeycloakToken, deleteKeycloakToken]);

	const clearMeProfile = useCallback(() => {
		setMeProfile(null);
		deleteMeProfile();
	}, [setMeProfile, deleteMeProfile]);

	const clearSrcmFirebaseId = useCallback(() => {
		setSrcmFirebaseId(null);
		deleteSrcmFirebaseId();
	}, [setSrcmFirebaseId, deleteSrcmFirebaseId]);

	const clearLoggedInUserRoles = useCallback(() => {
		setLoggedInUserRoles(null);
		deleteLoggedInUserRoles();
	}, [setLoggedInUserRoles, deleteLoggedInUserRoles]);

	const clearLoggedInUserPrivileges = useCallback(() => {
		setLoggedInUserPrivileges(null);
		deleteLoggedInUserPrivileges();
	}, [setLoggedInUserPrivileges, deleteLoggedInUserPrivileges]);

	const clearSelectedRole = useCallback(() => {
		setSelectedRole(null);
		deleteSelectedRole();
	}, [setSelectedRole, deleteSelectedRole]);

	const clearAllRolesWithModules = useCallback(() => {
		setAllRolesWithModules(null);
		deleteAllRolesWithModules();
	}, [setAllRolesWithModules, deleteAllRolesWithModules]);

	const clearAppInitiated = useCallback(() => {
		setAppInitiated(null);
		deleteAppInitiated();
	}, [setAppInitiated, deleteAppInitiated]);

	const clearCurrentActiveBatch = useCallback(() => {
		setCurrentActiveBatch(null);
		deleteCurrentActiveBatch();
	}, [setCurrentActiveBatch, deleteCurrentActiveBatch]);

	return {
		keycloakToken,
		setKeycloakToken,
		clearKeycloakToken,
		meProfile,
		setMeProfile,
		clearMeProfile,
		srcmFirebaseId,
		setSrcmFirebaseId,
		clearSrcmFirebaseId,
		loggedInUserRoles,
		setLoggedInUserRoles,
		clearLoggedInUserRoles,
		selectedRole,
		setSelectedRole,
		clearSelectedRole,
		allRolesWithModules,
		setAllRolesWithModules,
		clearAllRolesWithModules,
		appInitiated,
		setAppInitiated,
		clearAppInitiated,
		currentActiveBatch,
		setCurrentActiveBatch,
		clearCurrentActiveBatch,
		loggedInUserPrivileges,
		setLoggedInUserPrivileges,
		clearLoggedInUserPrivileges,
	};
}
